import React, { useRef } from "react";
import { useLocalization, Localized } from "@fluent/react";
import { RadioButtons, Button } from "@narmi/design_system";
import { ContextForm, CsrfTokenMiddleware } from "cerulean";
import "./authentication.css";

type OtpDeviceChoice = [string, string];

type MfaDeviceSelectProps = {
  otpDeviceChoices: OtpDeviceChoice[];
};

const MfaDeviceSelect = ({ otpDeviceChoices }: MfaDeviceSelectProps) => {
  const { l10n } = useLocalization();
  const form = useRef<HTMLFormElement>(null);
  const tfaRequiredButNotDevice = otpDeviceChoices.length === 0;

  const queryParams = new URLSearchParams(window.location.search);
  const next = queryParams.get("next");
  const redirectUrl = next ? encodeURIComponent(next) : "/";

  return (
    <div className="authentication-container">
      {!tfaRequiredButNotDevice ? (
        <form
          ref={form}
          action={`/login/verify_device_select?next=${redirectUrl}`}
          autoComplete="off"
          method="post"
        >
          <Localized id="heading-verify-account">
            <h1 data-testid="mfa-verify-header">
              Let&apos;s verify your account
            </h1>
          </Localized>
          <div>
            <Localized id="subhead-choose-verification">
              How would you like to receive your verification code?
            </Localized>
          </div>
          <CsrfTokenMiddleware />
          <div className="verify-device-options">
            <RadioButtons
              name="otp_device"
              testId="otp-device-radio-buttons"
              initialValue={otpDeviceChoices[0][0]}
              options={otpDeviceChoices.reduce<Record<string, string>>(
                (a, v, i) => {
                  const display = v[1];
                  // Append spaces so keys are always unique
                  // (Occasionally obfuscated phone numbers can look the same)
                  // e.g. "(555) 111-1111" & "(800) 111-1111" both become "(***) ***-*111"
                  const key = `${display} ${" ".repeat(i)}`;
                  return { ...a, [key]: v[0] };
                },
                {}
              )}
            />
          </div>
          <ContextForm.ActionBar>
            <Button
              as="button"
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                e.preventDefault();
                form.current?.submit();
              }}
              label={l10n.getString("button-continue", null, "Continue")}
              testId="mfa-device-select-submit"
            />
          </ContextForm.ActionBar>
        </form>
      ) : (
        <Localized id="error-no-devices">
          <p className="fontSize--heading3">
            You do not have any two-factor authentication devices. Please
            contact support for assistance.
          </p>
        </Localized>
      )}
    </div>
  );
};

export default MfaDeviceSelect;
